import { Injectable } from '@angular/core';

export type AuditDeviceCategory = 'Desktop' | 'Tablet' | 'Mobile' | 'Unknown';

export interface AuditEventData {
  schemaVersion: 1;
  operatingSystem: string;
  browserName: string;
  browserVersion: string;
  deviceCategory: AuditDeviceCategory;
  viewportWidth: number | null;
  viewportHeight: number | null;
  screenWidth: number | null;
  screenHeight: number | null;
  devicePixelRatio: number | null;
}

type UserAgentBrand = {
  brand?: string;
  version?: string;
};

type UserAgentData = {
  brands?: UserAgentBrand[];
  mobile?: boolean;
  platform?: string;
};

type NavigatorWithUserAgentData = Navigator & {
  userAgentData?: UserAgentData;
};

@Injectable({
  providedIn: 'root',
})
export class AuditEventDataService {
  getLoginEventData(): AuditEventData {
    const currentWindow = typeof window === 'undefined' ? null : window;
    const currentNavigator = typeof navigator === 'undefined' ? null : (navigator as NavigatorWithUserAgentData);
    const userAgent = currentNavigator?.userAgent ?? '';
    const userAgentData = currentNavigator?.userAgentData;
    const isIpadOs = this.isIpadOs(currentNavigator);
    const operatingSystem = this.getOperatingSystem(userAgent, userAgentData?.platform, isIpadOs);
    const browser = this.getBrowser(userAgent, userAgentData?.brands);

    return {
      schemaVersion: 1,
      operatingSystem,
      browserName: browser.name,
      browserVersion: browser.version,
      deviceCategory: this.getDeviceCategory(userAgent, userAgentData, isIpadOs),
      viewportWidth: this.normalizeDimension(currentWindow?.innerWidth),
      viewportHeight: this.normalizeDimension(currentWindow?.innerHeight),
      screenWidth: this.normalizeDimension(currentWindow?.screen?.width),
      screenHeight: this.normalizeDimension(currentWindow?.screen?.height),
      devicePixelRatio: this.normalizePixelRatio(currentWindow?.devicePixelRatio),
    };
  }

  private isIpadOs(currentNavigator: NavigatorWithUserAgentData | null): boolean {
    return currentNavigator?.platform === 'MacIntel' && (currentNavigator.maxTouchPoints ?? 0) > 1;
  }

  private getOperatingSystem(userAgent: string, platform: string | undefined, isIpadOs: boolean): string {
    if (isIpadOs) {
      return 'iPadOS';
    }

    const normalizedPlatform = platform?.toLowerCase() ?? '';
    if (normalizedPlatform.includes('windows')) {
      return 'Windows';
    }
    if (normalizedPlatform.includes('mac')) {
      return 'macOS';
    }
    if (
      normalizedPlatform.includes('ios') ||
      normalizedPlatform.includes('iphone') ||
      normalizedPlatform.includes('ipad')
    ) {
      return 'iOS';
    }
    if (normalizedPlatform.includes('android')) {
      return 'Android';
    }
    if (normalizedPlatform.includes('chrome')) {
      return 'ChromeOS';
    }
    if (normalizedPlatform.includes('linux')) {
      return 'Linux';
    }

    if (/Windows NT/i.test(userAgent)) {
      return 'Windows';
    }
    if (/Android/i.test(userAgent)) {
      return 'Android';
    }
    if (/iPhone|iPad|iPod/i.test(userAgent)) {
      return 'iOS';
    }
    if (/CrOS/i.test(userAgent)) {
      return 'ChromeOS';
    }
    if (/Mac OS X|Macintosh/i.test(userAgent)) {
      return 'macOS';
    }
    if (/Linux/i.test(userAgent)) {
      return 'Linux';
    }
    return 'Unknown';
  }

  private getBrowser(userAgent: string, brands: UserAgentBrand[] | undefined): { name: string; version: string } {
    const browserBrandMatchers: Array<{ name: string; pattern: RegExp }> = [
      { name: 'Edge', pattern: /edge|edg/i },
      { name: 'Opera', pattern: /opera|opr/i },
      { name: 'Samsung Internet', pattern: /samsung/i },
      { name: 'Firefox', pattern: /firefox/i },
      { name: 'Chrome', pattern: /chrome|google chrome/i },
      { name: 'Chromium', pattern: /chromium/i },
      { name: 'Safari', pattern: /safari/i },
    ];

    for (const matcher of browserBrandMatchers) {
      const matchingBrand = brands?.find((brand) => matcher.pattern.test(brand.brand ?? ''));
      if (matchingBrand) {
        return {
          name: matcher.name,
          version: this.normalizeVersion(matchingBrand.version),
        };
      }
    }

    const userAgentBrowser = [
      { name: 'Edge', pattern: /(?:Edg|Edge)\/([\d.]+)/i },
      { name: 'Opera', pattern: /(?:OPR|Opera)\/([\d.]+)/i },
      { name: 'Samsung Internet', pattern: /SamsungBrowser\/([\d.]+)/i },
      { name: 'Firefox', pattern: /Firefox\/([\d.]+)/i },
      { name: 'Chrome', pattern: /(?:Chrome|CriOS)\/([\d.]+)/i },
      { name: 'Safari', pattern: /Version\/([\d.]+).*Safari/i },
    ];

    for (const browserMatcher of userAgentBrowser) {
      const match = userAgent.match(browserMatcher.pattern);
      if (match) {
        return {
          name: browserMatcher.name,
          version: this.normalizeVersion(match[1]),
        };
      }
    }

    return { name: 'Unknown', version: 'Unknown' };
  }

  private getDeviceCategory(
    userAgent: string,
    userAgentData: UserAgentData | undefined,
    isIpadOs: boolean
  ): AuditDeviceCategory {
    if (isIpadOs || /iPad|Tablet/i.test(userAgent) || (/Android/i.test(userAgent) && !/Mobile/i.test(userAgent))) {
      return 'Tablet';
    }
    if (userAgentData?.mobile === true || /Android|iPhone|iPod|Mobile/i.test(userAgent)) {
      return 'Mobile';
    }
    if (userAgent || userAgentData) {
      return 'Desktop';
    }
    return 'Unknown';
  }

  private normalizeVersion(version: string | undefined): string {
    const normalizedVersion = version?.replace(/[^\x20-\x7E]/g, '').trim();
    return normalizedVersion ? normalizedVersion.slice(0, 32) : 'Unknown';
  }

  private normalizeDimension(value: number | undefined): number | null {
    return value !== undefined && Number.isFinite(value) && value > 0 ? Math.round(value) : null;
  }

  private normalizePixelRatio(value: number | undefined): number | null {
    return value !== undefined && Number.isFinite(value) && value > 0 ? Math.round(value * 100) / 100 : null;
  }
}
